import type { canadaProvincesData } from 'app/views/common/SharedData'

export type CanadaProvince = (typeof canadaProvincesData)[number]['value']

interface AccountTeamMember {
  UserId: string
}

export interface Account {
  direction?: string
  id: string
  formPageId?: string
  lastModifiedDate?: string
  organisationsName?: string
  facebook?: string
  website?: string
  province?: CanadaProvince
  street?: string
  postalCode?: string
  focusDemographic?: string[]
  additionalFeatures?: string[]
  members?: AccountTeamMember[]
  organisationType?: string[]
  recordType?: {
    name: string | null
  }
  ownerId?: string
  phone?: string
  email?: string
  linkedIn?: string
  employees?: number
  yearlyBudget?: number
  numberOfMembers?: number
  mainFundingSource?: string
  numberOfTenants?: number
  units?: number
  missionStatement?: string
  numberOfOrganizations?: number
  indigenousAffiliation?: string[]
  typeOfPopulation?: string[]
}

export interface SFAccount {
  direction?: string
  Id: string
  Name: string
  Form_Page__c?: string
  LastModifiedDate?: string
  Facebook__c?: string
  Website?: string
  BillingState?: string
  BillingStreet?: string
  BillingPostalCode?: string
  Focus_demographic__c?: string
  Additional_Features__c?: string
  AccountTeamMembers?: {
    records: AccountTeamMember[]
  }
  OwnerId?: string
  Phone?: string
  Email__c?: string
  LinkedIn__c?: string
  NumberOfEmployees: number
  Total_yearly_budget__c: number
  npo02__NumberOfMembershipOpps__c: number
  Main_funding_sources__c: string
  Number_of_tenants_living_in_your_buildin__c: number
  Housing_units_managed_by_Organisation__c: number
  Mission_Statement__c: string
  Number_of_member_organizations__c: number
  Indigenous_Organization__c: string
  Demography__c: string
}

export interface ICCEAccount {
  direction?: string
  id: string
  organisationsName?: string
  instagram?: string
  twitter?: string
  facebook?: string
  website?: string
  street?: string
  postalCode?: string
  focusDemographic?: string[]
  members?: string[]
  organisationType?: string[]
  recordType?: {
    name: string | null
  }
  ownerId?: string
  phone?: string
  email?: string
  linkedIn?: string
  employees?: number
  yearlyBudger?: number
  numberOfMembers?: number
  mainFundingSource?: string
  numberOfTenants?: number
  units?: number
  missionStatement?: string
  numberOfOrganizations?: number
  indigenousAffiliation?: string[]
  typeOfPopulation?: string[]
}

export interface SFICCEAccount {
  direction?: string
  Id: string
  Name: string
  Instagram__c?: string
  Twitter__c?: string
  Facebook__c?: string
  Website?: string
  BillingStreet?: string
  BillingPostalCode?: string
  Focus_demographic__c?: string
  AccountTeamMembers?: {
    records: AccountTeamMember[]
  }
  Account_Type__c?: string
  OwnerId?: string
  Phone?: string
  Email__c?: string
  LinkedIn__c?: string
  NumberOfEmployees?: number
  Total_yearly_budget__c?: number
  npo02__NumberOfMembershipOpps__c?: number
  Main_funding_sources__c?: string
  Number_of_tenants_living_in_your_buildin__c?: number
  Housing_units_managed_by_Organisation__c?: number
  Mission_Statement__c?: string
  Number_of_member_organizations__c?: number
  Indigenous_Organization__c?: string
  Demography__c?: string
}

export const accountFieldsMapSF = {
  direction: 'in',
  Id: 'id',
  Form_Page__c: 'formPageId',
  LastModifiedDate: 'lastModifiedDate',
  Name: 'organisationsName',
  Facebook__c: 'facebook',
  Website: 'website',
  BillingState: 'province',
  BillingStreet: 'street',
  BillingPostalCode: 'postalCode',
  Focus_demographic__c: {
    key: 'focusDemographic',
    in: (opp) => {
      const value = opp.Focus_demographic__c

      if (
        value === 'No focus demographic' ||
        value === undefined ||
        value === null
      ) {
        return []
      }

      return (value as string).split(';')
    },
    out: (res) => {
      if (res.hasFocusDemographic === 'No') {
        return 'No focus demographic'
      }
      return res.focusDemographic.join(';')
    }
  },
  Additional_Features__c: {
    key: 'additionalFeatures',
    in: (opp) =>
      (opp.Additional_Features__c as string)
        ? opp.Additional_Features__c.split(';')
        : [],
    out: (res) => {
      return res.additionalFeatures.join(';')
    }
  },
  AccountTeamMembers: {
    key: 'members',
    in: (acc: SFAccount): AccountTeamMember[] =>
      acc.AccountTeamMembers?.records ?? []
  },
  Account_Type__c: {
    key: 'organisationType',
    in: (opp) =>
      (opp.Account_Type__c as string) ? opp.Account_Type__c.split(';') : [],
    out: (res) => {
      return res.organisationType.join(';')
    }
  },
  RecordType: {
    key: 'recordType',
    in: (acc) => ({
      name: (acc.RecordType?.Name as string) || null
    })
  },
  OwnerId: 'ownerId',
  Phone: 'phone',
  Email__c: 'email',
  LinkedIn__c: 'linkedIn',
  NumberOfEmployees: 'employees',
  Total_yearly_budget__c: 'yearlyBudget',
  npo02__NumberOfMembershipOpps__c: 'numberOfMembers',
  Main_funding_sources__c: 'mainFundingSource',
  Number_of_tenants_living_in_your_buildin__c: 'numberOfTenants',
  Housing_units_managed_by_Organisation__c: 'units',
  Mission_Statement__c: 'missionStatement',
  Number_of_member_organizations__c: 'numberOfOrganizations',
  Indigenous_Organization__c: {
    key: 'indigenousAffiliation',
    in: (opp) =>
      (opp.Indigenous_Organization__c as string)
        ? opp.Indigenous_Organization__c.split(';')
        : [],
    out: (res) => res.indigenousAffiliation.join(';')
  },
  Demography__c: {
    key: 'typeOfPopulation',
    in: (opp) =>
      (opp.Demography__c as string) ? opp.Demography__c.split(';') : [],
    out: (res) => res.typeOfPopulation.join(';')
  }
}

export const ICCEAccountFieldsMapSF = {
  direction: 'in',
  Id: 'id',
  Name: 'organisationsName',
  Instagram__c: 'instagram',
  Twitter__c: 'twitter',
  Facebook__c: 'facebook',
  Website: 'website',
  BillingStreet: 'street',
  BillingPostalCode: 'postalCode',
  Focus_demographic__c: {
    key: 'focusDemographic',
    in: (opp) => {
      if (opp.Focus_demographic__c === 'No focus demographic') {
        return []
      }
      return (opp.Focus_demographic__c as string)
        ? opp.Focus_demographic__c.split(';')
        : []
    },
    out: (res) => {
      if (res.hasFocusDemographic === 'No') {
        return 'No focus demographic'
      }
      return res.focusDemographic.join(';')
    }
  },
  AccountTeamMembers: {
    key: 'members',
    in: (acc: SFICCEAccount): AccountTeamMember[] =>
      acc.AccountTeamMembers?.records ?? []
  },
  Account_Type__c: {
    key: 'organisationType',
    in: (opp) => (opp.Account_Type__c as string ? opp.Account_Type__c.split(';') : []),
    out: (res) => {
      return res.organisationType.join(';')
    }
  },
  OwnerId: 'ownerId',
  Phone: 'phone',
  Email__c: 'email',
  LinkedIn__c: 'linkedIn',
  NumberOfEmployees: 'employees',
  Total_yearly_budget__c: 'yearlyBudget',
  npo02__NumberOfMembershipOpps__c: 'numberOfMembers',
  Main_funding_sources__c: 'mainFundingSource',
  Number_of_tenants_living_in_your_buildin__c: 'numberOfTenants',
  Housing_units_managed_by_Organisation__c: 'units',
  Mission_Statement__c: 'missionStatement',
  Number_of_member_organizations__c: 'numberOfOrganizations',
  RecordType: {
    key: 'recordType',
    in: (acc) => ({
      name: acc.RecordType?.Name as string || null
    })
  },
  Indigenous_Organization__c: {
    key: 'indigenousAffiliation',
    in: (opp) =>
      opp.Indigenous_Organization__c as string
        ? opp.Indigenous_Organization__c.split(';')
        : [],
    out: (res) => res.indigenousAffiliation.join(';')
  },
  Demography__c: {
    key: 'typeOfPopulation',
    in: (opp) => (opp.Demography__c as string ? opp.Demography__c.split(';') : []),
    out: (res) => res.typeOfPopulation.join(';')
  }
}
